import { useOutletContext } from 'react-router-dom';
import {
  Shield,
  Mail,
  Cookie,
  Database,
  UserCheck,
  FileText,
  ShoppingBag,
  Calendar,
  Briefcase,
  Bell,
  Lock,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function PrivacyPolicy() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  return (
    <div className='min-h-screen py-12' data-testid='privacy-page'>
      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='text-center mb-12'>
          <div
            className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${
              isDark ? 'bg-blue-600/20' : 'bg-blue-100'
            }`}>
            <Shield
              className={`h-8 w-8 ${
                isDark ? 'text-blue-400' : 'text-blue-600'
              }`}
            />
          </div>
          <h1
            className={`text-3xl md:text-4xl font-black font-['Chivo'] mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
            Privacybeleid
          </h1>
          <p
            className={`max-w-2xl mx-auto ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}>
            Laatst bijgewerkt: januari 2025
          </p>
        </div>

        <div className='space-y-8'>
          {/* Introduction */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <FileText className='h-5 w-5 text-blue-600' />
                Inleiding
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>
                Futsal Gent ("wij", "ons" of "onze") hecht veel belang aan de
                bescherming van jouw privacy. In deze privacyverklaring leggen
                wij uit hoe wij jouw persoonsgegevens verzamelen, gebruiken,
                delen en beschermen wanneer je onze website bezoekt.
              </p>
              <p>
                Lees deze privacyverklaring aandachtig door. Indien je niet
                akkoord gaat met de voorwaarden van deze privacyverklaring,
                verzoeken wij je de website niet te gebruiken.
              </p>
            </CardContent>
          </Card>

          {/* Data Collection */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <Database className='h-5 w-5 text-blue-600' />
                Welke informatie wij verzamelen
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>
                Wij verzamelen verschillende soorten persoonsgegevens afhankelijk van hoe je onze website gebruikt:
              </p>

              {/* Shop Orders */}
              <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <ShoppingBag className='h-4 w-4 text-blue-500' />
                  Bestellingen in de webshop
                </h4>
                <ul className='list-disc list-inside space-y-1 text-sm'>
                  <li>Volledige naam</li>
                  <li>E-mailadres</li>
                  <li>Telefoonnummer</li>
                  <li>Leveringsadres (straat, postcode, stad)</li>
                  <li>Bestelgeschiedenis en productvoorkeuren</li>
                </ul>
              </div>

              {/* Newsletter */}
              <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Bell className='h-4 w-4 text-blue-500' />
                  Nieuwsbrief
                </h4>
                <ul className='list-disc list-inside space-y-1 text-sm'>
                  <li>E-mailadres</li>
                  <li>Naam (optioneel)</li>
                  <li>Inschrijfdatum</li>
                </ul>
              </div>

              {/* Contact Form */}
              <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Mail className='h-4 w-4 text-blue-500' />
                  Contactformulier
                </h4>
                <ul className='list-disc list-inside space-y-1 text-sm'>
                  <li>Naam</li>
                  <li>E-mailadres</li>
                  <li>Onderwerp en inhoud van je bericht</li>
                </ul>
              </div>

              {/* Activity Registration */}
              <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Calendar className='h-4 w-4 text-blue-500' />
                  Inschrijving voor activiteiten (Sport Eldorado)
                </h4>
                <ul className='list-disc list-inside space-y-1 text-sm'>
                  <li>Naam</li>
                  <li>E-mailadres</li>
                  <li>Telefoonnummer (optioneel)</li>
                  <li>Eventuele opmerkingen</li>
                </ul>
              </div>

              {/* Job Applications */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Briefcase className='h-4 w-4 text-blue-500' />
                  Sollicitaties
                </h4>
                <ul className='list-disc list-inside space-y-1 text-sm'>
                  <li>Naam</li>
                  <li>E-mailadres</li>
                  <li>Telefoonnummer</li>
                  <li>Motivatiebrief</li>
                  <li>CV-bestand (indien geüpload)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <Cookie className='h-5 w-5 text-blue-600' />
                Cookies & Tracking
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>
                Wij gebruiken cookies en vergelijkbare technologieën om het
                gebruik van onze website te analyseren en bepaalde informatie op
                te slaan. Bij je eerste bezoek vragen wij je toestemming via onze cookiebanner.
              </p>

              <h4
                className={`font-semibold mb-2 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                Welke cookies wij gebruiken
              </h4>
              <ul className='space-y-3 mb-4'>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Noodzakelijke cookies:
                  </strong>{' '}
                  Essentieel voor de goede werking van de website. Deze cookies onthouden je cookievoorkeuren, 
                  thema-instellingen (donker/licht), winkelmandje en inloggegevens voor het admin panel. 
                  Deze cookies kunnen niet worden uitgeschakeld.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Analytische cookies:
                  </strong>{' '}
                  Helpen ons te begrijpen hoe bezoekers onze website gebruiken. 
                  Deze informatie wordt anoniem verzameld en helpt ons de website te verbeteren.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Marketing cookies:
                  </strong>{' '}
                  Worden gebruikt om relevante advertenties te tonen en de effectiviteit 
                  van advertentiecampagnes te meten op sociale media platforms.
                </li>
              </ul>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Wat wij opslaan in je browser (localStorage)
                </h4>
                <ul className='list-disc list-inside space-y-1 text-sm'>
                  <li><strong>Thema-voorkeur:</strong> Donkere of lichte modus</li>
                  <li><strong>Cookie-toestemming:</strong> Je gekozen cookievoorkeuren</li>
                  <li><strong>Winkelmandje:</strong> Producten die je hebt toegevoegd</li>
                  <li><strong>Admin token:</strong> Beveiligde toegang tot het beheergedeelte (alleen voor beheerders)</li>
                </ul>
              </div>

              <p className='mt-4'>
                Je kunt je cookievoorkeuren op elk moment aanpassen via de cookiebanner onderaan de pagina, 
                of door de gegevens in je browser te wissen.
              </p>
            </CardContent>
          </Card>

          {/* Data Usage */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <UserCheck className='h-5 w-5 text-blue-600' />
                Hoe wij je gegevens gebruiken
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>
                Wij gebruiken de informatie die wij verzamelen voor de volgende doeleinden:
              </p>

              <ul className='list-disc list-inside space-y-2'>
                <li><strong className={isDark ? 'text-white' : 'text-slate-900'}>Bestellingen:</strong> Verwerken, verzenden en opvolgen van je bestellingen uit onze webshop</li>
                <li><strong className={isDark ? 'text-white' : 'text-slate-900'}>Nieuwsbrief:</strong> Versturen van nieuws over wedstrijden, activiteiten en clubnieuws (alleen met je toestemming)</li>
                <li><strong className={isDark ? 'text-white' : 'text-slate-900'}>Communicatie:</strong> Beantwoorden van je vragen via het contactformulier</li>
                <li><strong className={isDark ? 'text-white' : 'text-slate-900'}>Activiteiten:</strong> Beheren van inschrijvingen voor Sport Eldorado activiteiten</li>
                <li><strong className={isDark ? 'text-white' : 'text-slate-900'}>Sollicitaties:</strong> Verwerken van je sollicitatie voor een vacature</li>
                <li><strong className={isDark ? 'text-white' : 'text-slate-900'}>Statusupdates:</strong> E-mailnotificaties over de status van je bestelling</li>
                <li><strong className={isDark ? 'text-white' : 'text-slate-900'}>Verbetering:</strong> Analyseren en verbeteren van onze website en diensten</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <Clock className='h-5 w-5 text-blue-600' />
                Bewaartermijnen
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>
                Wij bewaren je gegevens niet langer dan nodig is voor de doeleinden waarvoor ze zijn verzameld:
              </p>

              <ul className='space-y-3'>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Bestellingen:</strong>{' '}
                  7 jaar na de laatste bestelling (wettelijke verplichting voor financiële administratie)
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Nieuwsbrief:</strong>{' '}
                  Tot je je afmeldt
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Contactberichten:</strong>{' '}
                  1 jaar na afhandeling
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Sollicitaties:</strong>{' '}
                  4 weken na afronding procedure, tenzij je toestemming geeft voor langere bewaring
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Activiteit inschrijvingen:</strong>{' '}
                  6 maanden na de activiteit
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <Lock className='h-5 w-5 text-blue-600' />
                Beveiliging van je gegevens
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>
                Wij nemen de beveiliging van je persoonsgegevens serieus en hebben passende maatregelen genomen:
              </p>

              <ul className='list-disc list-inside space-y-2'>
                <li>Beveiligde HTTPS-verbinding voor alle gegevensoverdracht</li>
                <li>Versleutelde opslag van wachtwoorden (bcrypt hashing)</li>
                <li>Beveiligde JWT-tokens voor authenticatie</li>
                <li>Beperkte toegang tot persoonsgegevens (alleen geautoriseerde beheerders)</li>
                <li>Regelmatige back-ups van de database</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Protection Rights */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <Shield className='h-5 w-5 text-blue-600' />
                Uw rechten inzake gegevensbescherming
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>Volgens de AVG (GDPR) heb je de volgende rechten:</p>
              <ul className='space-y-3'>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Recht op inzage:
                  </strong>{' '}
                  Je kunt een kopie van jouw persoonsgegevens opvragen.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Recht op rectificatie:
                  </strong>{' '}
                  Je kunt vragen om onjuiste of onvolledige gegevens te laten
                  corrigeren.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Recht op gegevenswissing:
                  </strong>{' '}
                  Je kunt onder bepaalde voorwaarden vragen om jouw
                  persoonsgegevens te laten verwijderen.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Recht op beperking van de verwerking:
                  </strong>{' '}
                  Je kunt onder bepaalde voorwaarden vragen om de verwerking te
                  beperken.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Recht op gegevensoverdraagbaarheid:
                  </strong>{' '}
                  Je kunt vragen om jouw gegevens over te dragen aan een andere
                  organisatie.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Recht van bezwaar:
                  </strong>{' '}
                  Je kunt bezwaar maken tegen de verwerking van jouw
                  persoonsgegevens.
                </li>
                <li>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Recht om toestemming in te trekken:
                  </strong>{' '}
                  Je kunt je toestemming voor de nieuwsbrief of marketing cookies op elk moment intrekken.
                </li>
              </ul>

              <p className='mt-4'>
                Je kunt ook een klacht indienen bij de Gegevensbeschermingsautoriteit (GBA) 
                als je van mening bent dat wij je gegevens onrechtmatig verwerken.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card
            className={
              isDark
                ? 'bg-slate-900/50 border-white/10'
                : 'bg-white border-slate-200 shadow-md'
            }>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-3 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}>
                <Mail className='h-5 w-5 text-blue-600' />
                Neem contact met ons op
              </CardTitle>
            </CardHeader>
            <CardContent
              className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              <p className='mb-4'>
                Indien u vragen heeft over deze privacyverklaring of uw rechten
                inzake gegevensbescherming wenst uit te oefenen, kunt u contact
                met ons opnemen via:
              </p>
              <div className='space-y-2'>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    E-mailadres:
                  </strong>{' '}
                  info@futsalgent.be
                </p>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Adres:
                  </strong>{' '}
                  Futsal Gent, Ter Linden 29, 9052 Gent, België
                </p>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>
                    Telefoon:
                  </strong>{' '}
                  +32 477 98 70 47
                </p>
              </div>

              <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  <strong>Tip:</strong> Voor vragen over je nieuwsbrief-abonnement kun je je ook direct uitschrijven 
                  door contact met ons op te nemen via bovenstaand e-mailadres.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
